import React, { useState, useEffect } from "react";
import Navbar from "../components/Navbar";
import Hero from "../components/Hero";
import Footer from "../components/Footer";
import "./ProductDetail.css";
import { supabase } from "../supabase";

const ProductDetail = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState("all");
  const [isQuoteModalOpen, setIsQuoteModalOpen] = useState(false);

  // Lock scroll when quote modal is open
  useEffect(() => {
    if (isQuoteModalOpen) {
      const scrollPosition = window.scrollY;
      document.body.style.overflow = "hidden";
      document.body.style.position = "fixed";
      document.body.style.top = `-${scrollPosition}px`;
      document.body.style.width = "100%";

      return () => {
        const scrollY = parseInt(document.body.style.top || "0") * -1;
        document.body.style.overflow = "";
        document.body.style.position = "";
        document.body.style.top = "";
        document.body.style.width = "";
        window.scrollTo(0, scrollY);
      };
    }
  }, [isQuoteModalOpen]);

  // Fetch products and set up real-time subscription
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error } = await supabase
          .from("products")
          .select("*")
          .order("created_at", { ascending: false });

        if (error) throw error;
        setProducts(data);
      } catch (err) {
        console.error("Error fetching products:", err);
        setError("Échec du chargement des produits: " + err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();

    const subscription = supabase
      .channel("products_changes")
      .on(
        "postgres_changes",
        { event: "*", schema: "public", table: "products" },
        (payload) => {
          if (payload.eventType === "INSERT") {
            setProducts((prev) => [payload.new, ...prev]);
          } else if (payload.eventType === "UPDATE") {
            setProducts((prev) =>
              prev.map((item) =>
                item.id === payload.new.id ? payload.new : item
              )
            );
          } else if (payload.eventType === "DELETE") {
            setProducts((prev) =>
              prev.filter((item) => item.id !== payload.old.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscription);
    };
  }, []);

  // Handle "Demander un Devis" button click
  const handleQuoteRequest = () => {
    setIsQuoteModalOpen(true);
  };

  // Close quote modal
  const handleQuoteModalClose = () => {
    setIsQuoteModalOpen(false);
  };

  // Define fixed categories
  const categories = ["all", "Ciment", "Cole pro", "Brique", "Hourdis", "Plâtrière"];

  // Assign category to product based on name
  const getProductCategory = (productName) => {
    const name = productName.toLowerCase();
    if (name.includes("ciment")) return "Ciment";
    if (name.includes("cole pro")) return "Cole pro";
    if (name.includes("brique")) return "Brique";
    if (name.includes("hourdis")) return "Hourdis";
    if (name.includes("plâtrière")) return "Plâtrière";
    return "Other"; // Fallback for uncategorized products
  };

  // Define category order
  const categoryOrder = {
    Ciment: 1,
    "Cole pro": 2,
    Brique: 3,
    Hourdis: 4,
    Plâtrière: 5,
    Other: 6,
  };

  // Filter and sort products
  const filteredProducts =
    selectedCategory === "all"
      ? products
      : selectedCategory === "Ciment"
      ? products.filter((product) => product.name.toLowerCase().includes("ciment"))
      : selectedCategory === "Cole pro"
      ? products.filter((product) => product.name.toLowerCase().includes("cole pro"))
      : selectedCategory === "Brique"
      ? products.filter((product) => product.name.toLowerCase().includes("brique"))
      : selectedCategory === "Hourdis"
      ? products.filter((product) => product.name.toLowerCase().includes("hourdis"))
      : products.filter((product) => product.name.toLowerCase().includes("plâtrière"));

  // Sort products by category order
  const sortedProducts = filteredProducts.sort((a, b) => {
    const categoryA = getProductCategory(a.name);
    const categoryB = getProductCategory(b.name);
    return categoryOrder[categoryA] - categoryOrder[categoryB];
  });

  return (
    <>
      <Navbar />
      <Hero />
      <section className="product-categories" id="products">
        <div className="container">
          <h2 className="section-title">Nos Articles</h2>
          <p className="section-text">
            Découvrez notre gamme de matériaux de construction de haute qualité,
            conçus pour répondre aux besoins de vos projets, qu'ils soient grands
            ou petits. Chaque produit est sélectionné pour sa durabilité,
            performance et fiabilité.
          </p>
          <div className="category-filter">
            {categories.map((category) => (
              <button
                key={category}
                className={`filter-button ${
                  selectedCategory === category ? "active" : ""
                }`}
                onClick={() => setSelectedCategory(category)}
              >
                {category === "all" ? "Tous" : category}
              </button>
            ))}
          </div>
          {loading ? (
            <p className="loading-text">Chargement des produits...</p>
          ) : error ? (
            <p className="error-text">{error}</p>
          ) : (
            <div className="products-grid">
              {sortedProducts.map((product) => (
                <div key={product.id} className="product-card">
                  <img
                    src={product.image_url || "https://via.placeholder.com/160"}
                    alt={product.name}
                    className="product-image"
                  />
                  <div className="product-content">
                    <h3 className="product-name">{product.name}</h3>
                    <p className="product-info">
                      <strong>Type:</strong> {product.type}
                    </p>
                    <p className="product-info">
                      <strong>Poids:</strong> {product.poids}
                    </p>
                    <p className="product-info">
                      <strong>Résistance:</strong> {product.résistance}
                    </p>
                    <div className="product-actions">
                      <button
                        className="action-button quote-button"
                        onClick={handleQuoteRequest}
                      >
                        Demander un Devis
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
      {isQuoteModalOpen && (
        <div className="quote-modal">
          <div className="quote-modal-content">
            <div className="detail-header">
              <h3>Demander un Devis</h3>
              <button className="close-button" onClick={handleQuoteModalClose}>
                ×
              </button>
            </div>
            <div className="quote-body">
              <p className="quote-text">
                Contactez-nous au <strong>26 290 922</strong> pour obtenir des
                informations détaillées sur nos tarifs et services.
              </p>
            </div>
          </div>
        </div>
      )}
      <Footer />
    </>
  );
};

export default ProductDetail;